import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, ArrowLeft, Loader2, RefreshCw, CheckCircle2, ChevronRight, MessageCircle } from 'lucide-react';

const BUNDESLAENDER = [
  'Bayern', 'Baden-Württemberg', 'Niedersachsen', 'Nordrhein-Westfalen',
  'Hessen', 'Sachsen', 'Thüringen', 'Brandenburg', 'Rheinland-Pfalz',
  'Schleswig-Holstein', 'Sachsen-Anhalt', 'Mecklenburg-Vorpommern',
  'Saarland', 'Bremen', 'Hamburg', 'Berlin',
];

export function StepAssistent({ ansicht, onErgaenzen, onZurueck, loading, t }) {
  const [antworten, setAntworten] = useState({});
  const [freitextModus, setFreitextModus] = useState(false);
  const [freitext, setFreitext] = useState('');
  const assistenz = ansicht?.assistenz || {};
  const status = assistenz.status;

  // Geführte Fragen basierend auf Status
  const fragen = [];

  if (status === 'zu_kurz' || status === 'rueckfrage') {
    fragen.push({
      id: 'was',
      frage: t('gefuehrt.wasPassiert'),
      typ: 'auswahl',
      optionen: t('gefuehrt.wasPassiertOptionen'),
    });
    fragen.push({
      id: 'wo',
      frage: t('gefuehrt.woLiegt'),
      typ: 'auswahl',
      optionen: BUNDESLAENDER,
    });
    fragen.push({
      id: 'groesse',
      frage: t('gefuehrt.wieGross'),
      typ: 'auswahl',
      optionen: t('gefuehrt.wieGrossOptionen'),
    });
    fragen.push({
      id: 'dringlichkeit',
      frage: t('gefuehrt.wieDringend'),
      typ: 'auswahl',
      optionen: t('gefuehrt.wieDringendOptionen'),
    });
  }

  if (status === 'nicht_forstlich') {
    fragen.push({
      id: 'was',
      frage: t('gefuehrt.wasPassiert'),
      typ: 'auswahl',
      optionen: t('gefuehrt.wasPassiertOptionen'),
    });
  }

  // Aktuelle Frage bestimmen (erste unbeantwortete)
  const aktuelleFrageIndex = fragen.findIndex(f => !antworten[f.id]);
  const aktuelleFrage = aktuelleFrageIndex >= 0 ? fragen[aktuelleFrageIndex] : null;
  const alleBeantwortet = fragen.length > 0 && aktuelleFrageIndex === -1;

  const handleAntwort = (fragenId, wert) => {
    setAntworten(prev => ({ ...prev, [fragenId]: wert }));
  };

  const handleAbsenden = () => {
    // Antworten zu einem natürlichen Text zusammenbauen
    const teile = [];
    if (antworten.was) teile.push(antworten.was);
    if (antworten.wo) teile.push(`Bundesland: ${antworten.wo}`);
    if (antworten.groesse) teile.push(`Fläche: ${antworten.groesse}`);
    if (antworten.dringlichkeit) teile.push(`Dringlichkeit: ${antworten.dringlichkeit}`);

    const text = teile.join('. ') + '.';
    onErgaenzen(text);
  };

  const handleFreitextAbsenden = () => {
    if (freitext.trim().length >= 10) {
      onErgaenzen(freitext.trim());
    }
  };

  // Titel basierend auf Status
  const titel = {
    zu_kurz: t('gefuehrt.titelZuKurz'),
    nicht_forstlich: t('gefuehrt.titelNichtForstlich'),
    rueckfrage: t('gefuehrt.titelRueckfrage'),
  }[status] || t('gefuehrt.titelRueckfrage');

  return (
    <div className="space-y-4" data-testid="step-assistent">
      <Card className="border-amber-200/80 shadow-soft">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
              <MessageCircle className="h-4 w-4 text-amber-700" />
            </div>
            <div>
              <h2 className="font-serif text-lg font-bold text-foreground" data-testid="assistent-status-titel">
                {titel}
              </h2>
              <p className="text-xs text-muted-foreground">{t('gefuehrt.intro')}</p>
            </div>
          </div>

          {/* Freitext-Modus Toggle */}
          {!freitextModus ? (
            <>
              {/* Bereits beantwortete Fragen (kompakt) */}
              {Object.keys(antworten).length > 0 && (
                <div className="mb-4 space-y-1.5" data-testid="bisherige-antworten">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] text-muted-foreground font-medium">{t('gefuehrt.bisherGesammelt')}</p>
                    <button
                      onClick={() => setAntworten({})}
                      className="text-[10px] text-amber-600 hover:text-amber-800 transition-colors"
                      data-testid="antworten-reset-btn"
                    >
                      {t('gefuehrt.allesZuruecksetzen')}
                    </button>
                  </div>
                  {Object.entries(antworten).map(([key, wert]) => (
                    <div key={key} className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-50 border border-emerald-200">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                      <span className="text-xs text-emerald-800 font-medium">{wert}</span>
                      <button
                        onClick={() => setAntworten(prev => { const copy = { ...prev }; delete copy[key]; return copy; })}
                        className="ml-auto text-[10px] text-emerald-600 hover:text-emerald-800"
                      >
                        {t('fa.aendern')}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Aktuelle Frage */}
              {aktuelleFrage && (
                <div className="mb-4" data-testid={`frage-${aktuelleFrage.id}`}>
                  <p className="text-sm font-medium text-foreground mb-3">
                    {aktuelleFrage.frage}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {aktuelleFrage.optionen.map((opt, i) => (
                      <button
                        key={i}
                        onClick={() => handleAntwort(aktuelleFrage.id, opt)}
                        className="px-3 py-2 text-sm rounded-lg border border-warm-200 bg-white text-foreground hover:border-primary/40 hover:bg-primary/[0.03] transition-all"
                        data-testid={`option-${aktuelleFrage.id}-${i}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Alle beantwortet → Absenden */}
              {alleBeantwortet && (
                <Button
                  onClick={handleAbsenden}
                  disabled={loading}
                  className="w-full h-11"
                  data-testid="gefuehrt-absenden-btn"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('fa.assistWirdAnalysiert')}</>
                  ) : (
                    <><RefreshCw className="h-4 w-4 mr-2" /> {t('gefuehrt.uebernehmen')}</>
                  )}
                </Button>
              )}

              {/* Wechsel zu Freitext */}
              <div className="mt-4 pt-3 border-t border-warm-200">
                <button
                  onClick={() => setFreitextModus(true)}
                  className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
                  data-testid="freitext-modus-btn"
                >
                  <ChevronRight className="h-3 w-3" />
                  {t('gefuehrt.oderFreitext')}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Freitext-Modus */}
              <div className="space-y-3">
                <Textarea
                  value={freitext}
                  onChange={(e) => setFreitext(e.target.value)}
                  placeholder={t('gefuehrt.freitextPlaceholder')}
                  className="min-h-[100px] text-sm resize-none"
                  data-testid="assistent-freitext-input"
                />
                <Button
                  onClick={handleFreitextAbsenden}
                  disabled={freitext.trim().length < 10 || loading}
                  className="w-full h-11"
                  data-testid="freitext-absenden-btn"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('fa.assistWirdAnalysiert')}</>
                  ) : (
                    <><RefreshCw className="h-4 w-4 mr-2" /> {t('fa.assistErneutAnalysieren')}</>
                  )}
                </Button>
              </div>
              <div className="mt-3">
                <button
                  onClick={() => setFreitextModus(false)}
                  className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
                >
                  <ArrowLeft className="h-3 w-3" />
                  {t('gefuehrt.zurueckZurEingabe')}
                </button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Button variant="outline" onClick={onZurueck} data-testid="assistent-zurueck-btn">
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t('fa.assistEingabeAendern')}
      </Button>
    </div>
  );
}
